/* eslint-disable camelcase */
/* eslint-disable no-restricted-globals */
import { ReportsProviderReducerState } from "@app-context/reports/context";
import { ReportRowDataEntry } from "@enfusion-ui/web-core";
import { SocketController, WebWorkerMessenger } from "@enfusion-ui/web-workers";
import { v4 as uuidv4 } from "uuid";

import { ModuleInterface } from "../../types/socket";

export type RawTableData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tableData: Record<string, any>;
};

export let reportProviderState: Omit<
  ReportsProviderReducerState,
  "dataStore" | "changeSubscriptions"
> &
  RawTableData = {
  openSubscriptions: {},
  metaStore: {},
  tableData: {},
};

export const setReportProviderState = (
  newReportProviderState: Omit<
    ReportsProviderReducerState,
    "dataStore" | "changeSubscriptions"
  > &
    RawTableData = {
    openSubscriptions: {},
    metaStore: {},
    tableData: {},
  }
) => (reportProviderState = newReportProviderState);

export const getReportProviderState = () => reportProviderState;

type ReportWebSocketCommands = "MESSAGE";

type ReportWebSocketMessage = {
  command: ReportWebSocketCommands;
  payload: any;
  destination: any;
};

const path = "/reports/v2";

const getSocketController = (messenger: WebWorkerMessenger, url: string) => {
  return new SocketController({
    path: `wss://${url}${path}`,
    onOpen: (socket) => {
      messenger.broadcast({
        command: "connected",
        payload: { readyState: socket.readyState },
      });
    },
    onMessage: async (event) => {
      const message = JSON.parse(event.data) as ReportWebSocketMessage;

      if (message.command === "MESSAGE") {
        const { payload } = message;
        switch (payload.type) {
          case "init-status": {
            const { tableId, progressSteps } = payload;
            const reportProviderState = getReportProviderState();

            const metaStoreEntry = reportProviderState.metaStore[tableId];
            reportProviderState.metaStore = {
              ...reportProviderState.metaStore,
              [tableId]: {
                ...metaStoreEntry,
                progressSteps,
              },
            };

            messenger.broadcast({
              command: "init-status",
              payload: { metaStore: reportProviderState.metaStore },
            });

            break;
          }

          case "table-sync": {
            const { destination } = message;
            const { tableId, rows } = payload;
            let reportProviderState = getReportProviderState();

            const metaStoreEntry = reportProviderState.metaStore[tableId];

            const tableDataWithUniqueRowIds = {
              ...payload,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              rows: rows.map((i: any, idx: number) => ({
                ...i,
                __row_id: uuidv4(),
                __row_idx: idx,
              })),
            };

            reportProviderState = {
              ...reportProviderState,
              metaStore: {
                ...reportProviderState.metaStore,
                [tableId]: metaStoreEntry
                  ? {
                      ...metaStoreEntry,
                      loading: false,
                      error: undefined,
                      destination,
                    }
                  : metaStoreEntry,
              },
              tableData: {
                ...reportProviderState.tableData,
                [tableId]: tableDataWithUniqueRowIds,
              },
            };

            setReportProviderState(reportProviderState);
            messenger.broadcast({
              command: "table-sync",
              payload: {
                metaStore: reportProviderState.metaStore,
                tableData: reportProviderState.tableData[tableId],
                reportId: tableId,
              },
            });

            break;
          }

          case "rows-update": {
            const { tableId, rowUpdates, totalsRow } = payload;
            const reportProviderState = getReportProviderState();
            const metaStoreEntry = reportProviderState.metaStore[tableId];
            const tableDataEntry = reportProviderState.tableData[tableId];

            const add: Array<ReportRowDataEntry> = [];
            if (tableDataEntry) {
              let newRows = [...tableDataEntry.rows];
              // eslint-disable-next-line @typescript-eslint/no-explicit-any

              for (const { columnValues, index, newRow } of rowUpdates) {
                // A new row gets spliced in at the given index
                if (newRow) {
                  const newRowData = {
                    __row_id: uuidv4(),
                    __row_idx: index,
                    ...columnValues,
                  };
                  add.push(newRowData);
                  newRows.splice(index, 0, newRowData);
                  continue;
                }

                // In existing rows, only given cells are overwritten
                newRows[index] = {
                  ...newRows[index],
                  ...columnValues,
                };
              }

              newRows = newRows.map(
                (i, idx) => ({ ...i, __row_idx: idx } as ReportRowDataEntry)
              );

              reportProviderState.tableData = {
                ...reportProviderState.tableData,
                [tableId]: {
                  ...tableDataEntry,
                  rows: newRows,
                  totalsRow: totalsRow || tableDataEntry.totalsRow,
                },
              };
            }

            reportProviderState.metaStore = {
              ...reportProviderState.metaStore,
              [tableId]: metaStoreEntry
                ? { ...metaStoreEntry, loading: false, error: undefined }
                : metaStoreEntry,
            };

            messenger.broadcast({
              command: "rows-update",
              payload: {
                metaStore: reportProviderState.metaStore,
                newRows: reportProviderState.tableData[tableId].rows,
                totalsRow: reportProviderState.tableData[tableId].totalsRow,
                tableId,
                add,
              },
            });
            break;
          }

          case "row-delete": {
            const { tableId } = payload;
            const reportProviderState = getReportProviderState();
            const metaStoreEntry = reportProviderState.metaStore[tableId];

            const tableDataEntry = reportProviderState.tableData[tableId];

            const { rowIndex } = tableDataEntry;

            if (tableDataEntry) {
              let newRows = [...(tableDataEntry.rows || [])];

              newRows = newRows.map(
                (i, idx) => ({ ...i, __row_idx: idx } as ReportRowDataEntry)
              );

              reportProviderState.tableData = {
                ...reportProviderState.tableData,
                [tableId]: {
                  ...tableDataEntry,
                  rows: newRows,
                },
              };
            }

            reportProviderState.metaStore = {
              ...reportProviderState.metaStore,
              [tableId]: metaStoreEntry
                ? { ...metaStoreEntry, loading: false, error: undefined }
                : metaStoreEntry,
            };

            messenger.broadcast({
              command: "row-delete",
              payload: {
                metaStore: reportProviderState.metaStore,
                newRows: reportProviderState.tableData[tableId].rows,
                tableId: tableId,
                rowIndex,
              },
            });

            break;
          }

          case "row-metadata": {
            const { destination } = message;
            const { metadata } = payload;

            messenger.broadcast({
              command: "row-metadata",
              payload: {
                destination,
                metadata,
              },
            });
            break;
          }

          case "stateError":
          case "error":
          case "argumentError":
          case "syncError": {
            const { reportId, message } = payload;
            const reportProviderState = getReportProviderState();
            const metaStoreEntry = reportProviderState.metaStore[reportId];

            reportProviderState.metaStore = {
              ...reportProviderState.metaStore,
              [reportId]: { ...metaStoreEntry, loading: false, error: message },
            };

            messenger.broadcast({
              command: "error",
              payload: {
                metaStore: reportProviderState.metaStore,
                reportId: reportId,
                error: message,
              },
            });
            break;
          }

          case "metadataError": {
            const { destination } = message;
            const { reportId } = payload; // reportId and message value are switched

            messenger.broadcast({
              command: "metadata-error",
              payload: {
                destination,
                message: reportId,
              },
            });
            break;
          }
        }
      }
    },
    onError: (err) => {
      console.error("Reports socket error", err);
    },
    onClose: (event) => {
      console.warn("Reports socket close", event);
    },
    onDisconnect: (socket) => {
      console.warn("Reports socket disconnect");
      socket.send(JSON.stringify({ command: "DISCONNECT" }));
    },
  });
};

export const ReportModule = () => {
  const messenger = new WebWorkerMessenger();
  let heartbeatInterval: NodeJS.Timeout;
  let socketController: SocketController<any>;

  const enable = (
    postMessage: (message: Record<string, unknown>) => void,
    url: string
  ) => {
    socketController = getSocketController(messenger, url);
    messenger.send = postMessage;
    socketController.init();
  };

  const disable = () => {
    socketController.close("reports");
  };

  const onTerminate = () => {
    socketController.close("reports");
  };

  const getCurrentState = () => {
    return reportProviderState;
  };

  const onMessage = (data: Record<string, any>) => {
    const { command, payload } = data;
    switch (command) {
      case "socket-open":
        {
          const { userName, passCode } = payload;
          const connectMessage = passCode.length
            ? {
                command: "CONNECT",
                login: userName,
                passcode: passCode,
                "heart-beat": 8000,
                "accept-version": "1.2",
              }
            : {
                command: "CONNECT",
                login: userName,
                "heart-beat": 8000,
                "accept-version": "1.2",
              };
          const message = JSON.stringify(connectMessage);
          socketController.send(message);
          messenger.send({
            command: "log-message",
            payload: message,
          });
          if (passCode.length)
            heartbeatInterval = setInterval(() => {
              socketController.send("\n");
              messenger.send({ command: "log-message", payload: "heartbeat" });
            }, 30000);
        }
        break;
      case "open-detail-table": {
        const { destination, params } = payload;
        socketController.send(
          JSON.stringify({
            command: "SEND",
            destination: destination,
            payload: params,
          })
        );
        break;
      }
      case "load-report": {
        const { path, reportId } = payload;
        const message = JSON.stringify({
          command: "SUBSCRIBE",
          destination: `/report-feed/report/${reportId}`,
          payload: { reportURL: path },
        });
        messenger.send({ command: "log-message", payload: message });
        socketController.send(message);
        break;
      }
      case "close-report": {
        const { reportIdToClose, tableIdToClose } = payload;
        const message = JSON.stringify({
          command: "SEND",
          destination: `/report-feed/report/${reportIdToClose}/${tableIdToClose}/close`,
        });
        messenger.send({ command: "log-message", payload: message });
        socketController.send(message);
        clearInterval(heartbeatInterval);
        break;
      }
      case "unsubscribe": {
        const { reportIdToClose } = payload;
        const message = JSON.stringify({
          command: "UNSUBSCRIBE",
          destination: `/report-feed/report/${reportIdToClose}`,
        });
        messenger.send({ command: "log-message", payload: message });
        socketController.send(message);
        break;
      }
    }
  };

  return {
    enable,
    disable,
    onTerminate,
    getCurrentState,
    onMessage,
  } as ModuleInterface;
};
